import { type Point, type UnitDirection, toPoint } from './toPortInfo.tsx';

/** Adds two Point together */
export const add = (point: Point, other: Point) => toPoint(point.x + other.x, point.y + other.y);

/** Subtracts two Point */
export const subtract = (point: Point, other: Point) =>
	toPoint(point.x - other.x, point.y - other.y);

/** Multiplies a Point by either a constant value. */
export const multiply = (point: Point, scalar: number) =>
	toPoint(point.x * scalar, point.y * scalar);

/**
 * Divides the Point by a constant value.
 *
 * Convenience function Equivalent to `multiply(point, 1 / scalar)` */
export const divide = (point: Point, scalar: number) => toPoint(point.x / scalar, point.y / scalar);

/** Provides the Point on a line that represents the % way there.
 * For example 50% would be the midpoint of the line.
 *
 * @param percent percentage value as a floating point, i.e. 0 to 1, where 1 is 100%
 */
export const percentageOfLine = (start: Point, end: Point, percent: number) =>
	toPoint((start.x + end.x) * percent, (start.y + end.y) * percent);

/** Provides the point where the middle of a line is */
export const midpoint = (start: Point, end: Point) => percentageOfLine(start, end, 0.5);

/** Inverts the direction of a Point relative to the origin
 *
 * @example
 * invert({ x: 10, y: 5})
 * // = { x: -10, y: -5 }
 */
export const invert = (point: Point) => multiply(point, -1);

/** Direct distance between two points */
export const distance = (p: Point, q: Point) => {
	const a = q.x - p.x;
	const b = q.y - p.y;
	return Math.sqrt(a * a + b * b);
};

/** Angle between two points as degrees, guaranteed to be between 0º <= angle < 360º */
export const angleBetween = (p: Point, q: Point): number => {
	const degrees = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;

	const angleClampedBetween0And360 = (degrees + 360) % 360;
	return angleClampedBetween0And360;
};

/** Provides the angle in degrees of a {@link UnitDirection} */
export const unitDirectionAngle = (unitDirection: UnitDirection): number =>
	(Math.atan2(unitDirection.x, unitDirection.y) * 180) / Math.PI;
